/**
 * Question screen typography (v2 QuestionWrapper flow).
 * AcknowledgmentQuestion uses its own styles and is excluded.
 *
 * HEADLINE — scales by plain-text character count (HTML entities count as one char):
 *   Desktop                          Mobile
 *   ≤60 chars:  28px                 24px
 *   61–140:     26px                 22px
 *   >140:       24px                 20px
 *   Weight 500, line-height 1.2 (tight)
 *
 * SUBHEADING — fixed size (no length scaling):
 *   Desktop: var(--text-base)    Mobile: var(--text-body-mobile)
 *   Weight 400 (bodyBase), line-height 1.5
 *
 * OPTIONS (radio/checkbox pills) — fixed size (no length scaling):
 *   Desktop: var(--text-base) font, 12px × 20px padding
 *   Mobile:  var(--text-body-mobile) font, 8px × 16px padding
 *   Weight 400 (inherited from label)
 *
 * Mobile breakpoint matches viewport.js: window.innerWidth < 768 (max-width: 767px in CSS).
 */

export const QUESTION_MOBILE_MAX_WIDTH_PX = 767;

export const QUESTION_HEADLINE_SHORT_MAX_LENGTH = 60;
export const QUESTION_HEADLINE_MEDIUM_MAX_LENGTH = 140;

export const QUESTION_HEADLINE_TIERS = [
  { mobileFontSize: 24, desktopFontSize: 28 },
  { mobileFontSize: 22, desktopFontSize: 26 },
  { mobileFontSize: 20, desktopFontSize: 24 },
];

export const QUESTION_SUBHEADING_FONT_SIZE = {
  desktop: 'var(--text-base)',
  mobile: 'var(--text-body-mobile)',
};

export const QUESTION_OPTION_FONT_SIZE = {
  desktop: 'var(--text-base)',
  mobile: 'var(--text-body-mobile)',
};

export const QUESTION_OPTION_PADDING = {
  desktop: { y: 12, x: 20 },
  mobile: { y: 8, x: 16 },
};

const HTML_ENTITY_REPLACEMENTS = [
  { pattern: /&rsquo;/gi, replacement: "'" },
  { pattern: /&lsquo;/gi, replacement: "'" },
  { pattern: /&rdquo;/gi, replacement: '"' },
  { pattern: /&ldquo;/gi, replacement: '"' },
  { pattern: /&amp;/gi, replacement: '&' },
  { pattern: /&nbsp;/gi, replacement: ' ' },
];

export function getQuestionHeadingTextLength(text) {
  if (text == null || text === '') return 0;

  let normalized = String(text);
  for (const { pattern, replacement } of HTML_ENTITY_REPLACEMENTS) {
    normalized = normalized.replace(pattern, replacement);
  }
  normalized = normalized.replace(/<[^>]*>/g, '');

  return normalized.length;
}

export function getQuestionHeadingScaleTier(text) {
  const length = getQuestionHeadingTextLength(text);

  if (length <= QUESTION_HEADLINE_SHORT_MAX_LENGTH) return QUESTION_HEADLINE_TIERS[0];
  if (length <= QUESTION_HEADLINE_MEDIUM_MAX_LENGTH) return QUESTION_HEADLINE_TIERS[1];
  return QUESTION_HEADLINE_TIERS[2];
}

export function getQuestionHeadingFontSize(text, { isMobile = false } = {}) {
  const tier = getQuestionHeadingScaleTier(text);
  return isMobile ? tier.mobileFontSize : tier.desktopFontSize;
}

export function getQuestionHeadingScale(text, { isMobile = false } = {}) {
  const fontSize = getQuestionHeadingFontSize(text, { isMobile });
  return {
    fontSize: `${fontSize}px`,
    lineHeight: 'var(--leading-tight)',
  };
}
